import { Link } from 'react-router-dom';
import { DocsNav } from '../components/DocsNav';
import {
  Card,
  CommandBlock,
  Disclosure,
  Eyebrow,
  Heading,
  Lead,
  ScrollTable,
  Section,
} from '../components/ui';
import { usePageTitle } from '../lib/usePageTitle';

/** Terminal output, shown as it appears. Not copyable: it is a result, not a
 *  command, and a copy button on it invites pasting output back as input. */
function Code({ children }: { children: string }) {
  return (
    <pre className="mt-3 overflow-x-auto rounded-lg border border-hull-700 bg-hull-950 p-4 font-mono text-xs leading-relaxed text-hull-200">
      <code>{children}</code>
    </pre>
  );
}

/**
 * The Security & Compliance reference.
 *
 * Two things this page has to do that a feature page does not. It has to say
 * precisely what each framework's automated verification *does not* establish —
 * a clean HIPAA section is not HIPAA compliance, and a reader who takes it that
 * way has been misled by us, not by themselves. And it has to be honest about
 * testability: most of ISO/IEC 27001 and a third of WCAG cannot be reached by
 * a browser at all, and the page says so beside each one rather than in a
 * footnote nobody reaches.
 */

const FLAGS = [
  { flag: '--uwas', framework: 'Universal Web Security', purpose: 'Core web application security' },
  { flag: '--cwsh', framework: 'Web Security Headers', purpose: 'HTTP and browser security headers' },
  { flag: '--owasp', framework: 'OWASP ASVS 5.0', purpose: 'Application security verification' },
  { flag: '--privacy', framework: 'Privacy', purpose: 'General personal-data protection' },
  { flag: '--gdpr', framework: 'GDPR', purpose: 'EU/EEA privacy' },
  { flag: '--ccpa', framework: 'CCPA/CPRA', purpose: 'California privacy' },
  { flag: '--hipaa', framework: 'HIPAA Security Rule', purpose: 'Healthcare and ePHI' },
  { flag: '--pci', framework: 'PCI DSS 4.0.1', purpose: 'Payment and cardholder data' },
  { flag: '--finance', framework: 'Financial Security', purpose: 'Financial applications' },
  { flag: '--iso27001', framework: 'ISO/IEC 27001:2022', purpose: 'Information-security controls' },
  { flag: '--accessibility', framework: 'WCAG 2.2 AA', purpose: 'Accessibility' },
  { flag: '--security', framework: 'Security bundle', purpose: 'Applicable security controls' },
  { flag: '--compliance-all', framework: 'All configured frameworks', purpose: 'Applicability-driven compliance' },
];

const STATES = [
  ['PASS', 'text-pass-400', 'The control was verified against a stored artefact. A control automation does not reach can never be recorded here.'],
  ['FAIL', 'text-fail-400', 'The check ran and the application did not satisfy the control. Reported with what was expected and what was found.'],
  ['BLOCKED', 'text-warn-400', 'The check could not run — the page would not render, the environment was unreachable. Not evidence of a defect.'],
  ['NOT_APPLICABLE', 'text-hull-400', 'The control was never in scope. A payment control on an application that handles no card data. Reported with the reason, not omitted.'],
  ['NOT_UI_TESTABLE', 'text-hull-400', 'No property of a web application could establish this. A security policy, a management review, an asset inventory.'],
  ['EVIDENCE_REQUIRED', 'text-hull-400', 'Settleable, but not from here. Needs a document, a configuration export, a log sample, or an authorised active test.'],
  ['ERROR', 'text-fail-400', 'The check itself failed. A tooling fault, not a statement about the application either way.'],
  ['SKIPPED', 'text-hull-400', 'Deliberately not run. Active probes against production, for instance, are refused whatever the configuration says.'],
];

const TEST_KINDS = [
  ['UI TEST', 'Browser interaction with the rendered page — structure, labels, form shape, consent controls.'],
  ['API TEST', 'HTTP interaction — status codes, response headers, redirect chains, TLS verification.'],
  ['NETWORK TEST', 'Inspection of the exchange itself — the certificate, the plain-HTTP behaviour, the cookie attributes.'],
  ['EVIDENCE TEST', 'A document, a configuration export, an audit-log sample. WaveQA names what it needs and does not guess.'],
  ['LLM TEST', 'Semantic interpretation, used only where reading prose against a requirement is the actual question.'],
  ['NOT UI TESTABLE', 'Cannot be established from the application at all. Reported as such rather than passed.'],
];

const UWAS_GROUPS = [
  ['Transport', ['HTTPS in use', 'HTTP redirects to HTTPS', 'Certificate validates for the host', 'HSTS configured', 'No plaintext subresources', 'Weak protocols and ciphers (evidence)']],
  ['Cookies', ['Secure', 'HttpOnly', 'SameSite', 'No readable payload in the value', 'Expiration and scope (evidence)']],
  ['Authentication', ['Credentials never posted over plaintext', 'No password in a GET form', 'No credential in a URL', 'Account enumeration (semantic)', 'Brute-force protection (active)', 'Password policy (evidence)', 'MFA availability (evidence)', 'Reset token single-use and expiring (evidence)']],
  ['Session management', ['No session token in web storage', 'Session fixation (active)', 'Idle and absolute timeout (evidence)', 'Logout invalidates server-side (evidence)']],
  ['Authorization', ['Unauthenticated access refused (active)', 'IDOR / BOLA (active)', 'Privilege escalation (active)', 'Tenant isolation (evidence)']],
  ['Input validation', ['Enforced CSP as the injected-script backstop', 'Cross-site scripting (active)', 'SQL and NoSQL injection (active)', 'Path traversal and command injection (active)', 'Server-side length and type validation (evidence)']],
  ['File handling', ['Upload type, size and filename constraints (evidence)', 'Download authorisation (evidence)']],
  ['API security', ['CORS is an allowlist, not a wildcard', 'Anti-forgery token on state-changing forms', 'Excessive data exposure (evidence)', 'Rate limiting (active)']],
  ['Error handling', ['No stack trace, database error or internal path in any response', 'Correct status codes per condition (evidence)']],
  ['Browser security', ['Clickjacking defence', 'No secret shipped to the browser', 'rel="noopener" on new-tab links', 'No production source maps', 'Third-party script inventory (evidence)', 'Open redirect (active)']],
];

const HEADERS = [
  ['Strict-Transport-Security', 'CWSH-001/002', 'Keeps the browser on HTTPS for subsequent requests. Checked for a real max-age, not just presence — max-age=0 is HSTS switched off.'],
  ['Content-Security-Policy', 'CWSH-003/004/005', 'The last line against injected script. Checked as a policy: enforced rather than report-only, and not permitting unsafe-inline or unsafe-eval where it matters.'],
  ['X-Content-Type-Options', 'CWSH-007', 'Stops a response being interpreted as a type it was not sent as.'],
  ['X-Frame-Options', 'CWSH-006', 'Restricts framing, so the interface cannot be overlaid and its clicks captured.'],
  ['Referrer-Policy', 'CWSH-008', 'Stops full URLs, and any identifiers in them, reaching third-party origins.'],
  ['Permissions-Policy', 'CWSH-009', 'Denies powerful browser features the application does not use.'],
  ['Set-Cookie', 'CWSH-012/013/014/015', 'Secure, HttpOnly and SameSite on session cookies, and no readable data in the value.'],
  ['Cache-Control', 'CWSH-011', 'Pages rendering personal data must not be retained by browsers or shared caches.'],
  ['Server / X-Powered-By', 'CWSH-010', 'Technology disclosure. Not a vulnerability — a free hint about which advisories to try.'],
];

const ASVS_CHAPTERS = [
  'Encoding and Sanitization',
  'Validation and Business Logic',
  'Web Frontend Security',
  'API and Web Service',
  'File Handling',
  'Authentication',
  'Session Management',
  'Authorization',
  'Self-contained Tokens',
  'OAuth and OIDC',
  'Cryptography',
  'Secure Communication',
  'Configuration',
  'Data Protection',
  'Secure Coding and Architecture',
  'Security Logging and Error Handling',
];

const GDPR_TOPICS = [
  ['Privacy notice', 'Reachable from the point of collection; controller, purposes and legal basis stated; retention and rights stated; recipients and international transfers stated.'],
  ['Consent', 'Not pre-selected. Granular and separable from terms. Withdrawable as easily as it was given. Recorded with a timestamp and a notice version.'],
  ['Cookies', 'A consent mechanism before non-essential storage. A cookie policy. Rejecting as easy as accepting. No tracking before consent.'],
  ['Data minimisation', 'Collection limited to the purpose. No personal identifier in a URL.'],
  ['Security of processing', 'TLS in transit. Nothing personal in web storage. No personal data in error output.'],
  ['Data subject rights', 'Access, rectification, erasure, restriction, objection and portability each have a route that takes effect.'],
  ['Accountability', 'Lawful basis recorded, Article 28 processor contracts, transfer mechanisms, DPIAs, breach procedure — all documentary, all reported as such.'],
];

const HIPAA_TOPICS = [
  ['Access control 164.312(a)', 'Unique user identification, ePHI unreachable unauthenticated, cross-patient access prevented, minimum necessary, automatic logoff.'],
  ['ePHI exposure', 'No patient identifier in a URL. Identifiers masked where displayed. Nothing in web storage. Clinical pages not cached. No ePHI in error output.'],
  ['Tracking technologies', 'Third-party trackers on pages rendering ePHI. WaveQA lists every third-party origin the page loaded so it can be checked against your BAAs.'],
  ['Transmission security 164.312(e)', 'TLS everywhere ePHI moves, valid certificate, plaintext refused, session cookies protected.'],
  ['Audit controls 164.312(b)', 'Access logging and log review. Not observable from the application — reported as EVIDENCE_REQUIRED with the four fields a log line needs.'],
  ['Administrative safeguards 164.308', 'Risk analysis, business associate agreements, workforce training, breach notification. All NOT_UI_TESTABLE.'],
];

const PCI_TOPICS = [
  ['Cardholder data', 'PAN masked when displayed. No card field in a URL. Nothing in web storage or cookies. No card data in error output.'],
  ['Sensitive authentication data', 'CVV, track data and PIN blocks must not be stored after authorisation. A storage question — reported as EVIDENCE_REQUIRED.'],
  ['Transport (Req. 4)', 'Strong cryptography for PAN in transit, valid certificate, plaintext refused, payment forms posting over TLS.'],
  ['Public-facing web (Req. 6.4)', 'Payment-page script inventory and integrity, a WAF in blocking mode, an enforced CSP on the payment page.'],
  ['Payment integrity', 'Server-side amount determination, authenticated webhooks, idempotent payment requests.'],
  ['Monitoring and scope (Req. 11, 12)', 'Tamper detection on the payment page, the security policy, and the CDE scoping exercise. Documentary.'],
];

const ISO_TESTABLE = [
  ['A.5.15 Access control', 'The application half is testable; the governing policy is not. Both are needed.'],
  ['A.5.17 Authentication information', 'Credentials never submitted over plaintext, plus the credential-storage configuration.'],
  ['A.8.5 Secure authentication', 'Session material protected from script, plus the identity provider configuration.'],
  ['A.8.9 Configuration management', 'No unnecessary technology disclosure, plus the documented baseline.'],
  ['A.8.12 Data leakage prevention', 'No credential disclosed through the interface, plus the wider DLP configuration.'],
  ['A.8.24 Use of cryptography', 'TLS in transit is observable. Key management is not, and is the half auditors examine.'],
  ['A.8.26 Application security requirements', 'The deployed application against its documented requirements.'],
];

const ISO_ORGANISATIONAL = [
  'A.5.1 Information security policies',
  'A.5.7 Threat intelligence',
  'A.5.9 Asset inventory',
  'A.5.12 Information classification',
  'A.5.19 Supplier relationships',
  'A.5.24 Incident management',
  'A.5.29 / A.5.30 Continuity and ICT readiness',
  'A.6.3 Security awareness training',
  'A.6.8 Event reporting',
  'A.8.8 Vulnerability management',
  'A.8.13 Backup',
  'A.8.15 Logging',
  'A.8.16 Monitoring',
  'A.8.32 Change management',
  'Clause 6.1 Risk assessment',
  'Clause 9.2 Internal audit',
  'Clause 9.3 Management review',
  'Clause 10.2 Nonconformity and improvement',
];

const WCAG_AUTOMATED = [
  '1.1.1 Images declare an alt attribute',
  '1.3.1 Form controls have a programmatic label',
  '1.3.1 Heading levels form a navigable sequence',
  '1.4.4 The viewport does not block zoom',
  '2.4.1 A skip link is present',
  '2.4.3 No positive tabindex overrides focus order',
  '2.4.4 Links have an accessible name',
  '3.1.1 The page declares its language',
  '3.3.1 A live region exists for validation',
  '4.1.2 Buttons have an accessible name',
  '4.1.2 Dialogs declare a dialog role',
];

const WCAG_HUMAN = [
  '1.4.3 Colour contrast, including against images and gradients',
  '2.1.1 All functionality operable from a keyboard',
  '2.1.2 No keyboard trap',
  '2.4.7 Focus is visibly indicated',
  '3.3.3 Error messages suggest a correction',
  '1.4.10 Content reflows at 320px',
  'Whether alternative text is meaningful',
  'Whether the page is comprehensible with a screen reader',
];

const PAGE_TYPES = [
  'PUBLIC', 'LOGIN', 'REGISTER', 'PASSWORD_RESET', 'MFA', 'DASHBOARD', 'PROFILE',
  'ACCOUNT', 'SEARCH', 'LIST', 'DETAIL', 'CREATE', 'EDIT', 'DELETE', 'UPLOAD',
  'DOWNLOAD', 'ADMIN', 'USER_MANAGEMENT', 'ROLE_MANAGEMENT', 'PERMISSION_MANAGEMENT',
  'AUDIT_LOG', 'PAYMENT', 'CHECKOUT', 'TRANSACTION', 'PATIENT', 'MEDICAL_RECORD',
  'INSURANCE', 'FINANCIAL_ACCOUNT', 'REPORT', 'EXPORT', 'SETTINGS', 'PRIVACY',
  'COOKIE', 'TERMS', 'ERROR',
];

const DATA_TYPES = [
  'PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'PERSONAL_DATA', 'SENSITIVE_PERSONAL_DATA',
  'PHI', 'EPHI', 'FINANCIAL_DATA', 'CARDHOLDER_DATA', 'AUTHENTICATION_DATA', 'SECRET',
];

const EVIDENCE = [
  'Screenshot', 'DOM snapshot', 'URL', 'HTTP request', 'HTTP response',
  'Response headers', 'Redirect chain', 'TLS verification result', 'Cookie attributes',
  'Browser storage keys and value shapes', 'Console output', 'Timestamp',
  'Expected result', 'Actual result',
];

function Pills({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-wrap gap-2">
      {items.map((item) => (
        <li
          key={item}
          className="rounded-md border border-hull-700 bg-hull-950 px-2.5 py-1 font-mono text-xs text-hull-300"
        >
          {item}
        </li>
      ))}
    </ul>
  );
}

function Checklist({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5">
      {items.map((item) => (
        <li key={item} className="flex gap-2.5 text-sm text-hull-300">
          <span aria-hidden="true" className="mt-0.5 shrink-0 text-surge-400">
            ✓
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function Topics({ rows }: { rows: string[][] }) {
  return (
    <dl className="space-y-4">
      {rows.map(([term, body]) => (
        <div key={term}>
          <dt className="text-sm font-semibold text-white">{term}</dt>
          <dd className="mt-1 text-sm leading-relaxed text-hull-300">{body}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The "what this does not establish" callout.
 *
 * Carries its own top margin because it always follows content and always needs
 * separating from it — a disclaimer flush against the cards above reads as part
 * of them rather than as a qualification of them.
 */
function Warning({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-6 rounded-card border border-warn-600/40 bg-warn-600/10 p-5 text-sm leading-relaxed text-hull-200">
      {children}
    </div>
  );
}

export function SecurityCompliance() {
  usePageTitle('Security & Compliance — WaveQA');

  return (
    <>
      <DocsNav />

      <Section className="border-b border-hull-800">
        <Eyebrow>Documentation</Eyebrow>
        <Heading level={1} className="text-white">
          Security &amp; Compliance
        </Heading>
        <Lead className="mt-5 max-w-3xl">
          Security, privacy, regulatory and accessibility verification, added to
          the feature test you already run. One flag attaches a framework&rsquo;s
          applicable controls to the same run, against the same pages, in the same
          report.
        </Lead>
        <CommandBlock className="mt-6 max-w-3xl" label="Copy example command">{`waveqa -p "DemoProject" -f "Feature-01" --dev --uwas --cwsh --owasp`}</CommandBlock>
      </Section>

      {/* --- What this is, and is not ------------------------------------ */}
      <Section className="border-b border-hull-800" id="what">
        <Heading className="text-white">What verification means here</Heading>
        <Lead className="mt-3 max-w-3xl">
          Three properties decide whether a compliance report is worth reading.
          These are ours.
        </Lead>

        <div className="mt-8 grid gap-4 md:grid-cols-3">
          <Card>
            <h3 className="text-base font-semibold text-white">No false pass</h3>
            <p className="mt-2 text-sm leading-relaxed text-hull-300">
              A control that automation cannot reach is never recorded as PASS.
              The rule is enforced where results are constructed, so no code path
              can produce one — an ISMS policy does not pass because the login
              page rendered.
            </p>
          </Card>
          <Card>
            <h3 className="text-base font-semibold text-white">
              Nothing irrelevant is applied
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-hull-300">
              PCI against an application that handles no card data resolves to
              NOT_APPLICABLE once, with the reason attached — not to twenty-five
              verdicts about data the product has never held.
            </p>
          </Card>
          <Card>
            <h3 className="text-base font-semibold text-white">
              Every result cites what it saw
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-hull-300">
              Expected, actual, and the artefact. A finding you can open is a
              defect report; a finding that says only &ldquo;FAIL&rdquo; is an
              argument nobody can settle.
            </p>
          </Card>
        </div>

        <Warning>
          <strong className="text-white">What this is not.</strong> Automated
          results are technical verification evidence. They do not by themselves
          constitute legal compliance, certification, accreditation, a penetration
          test, or regulatory approval. See the{' '}
          <a href="#disclaimer" className="text-surge-400 underline">
            disclaimer
          </a>
          .
        </Warning>
      </Section>

      {/* --- Flags -------------------------------------------------------- */}
      <Section className="border-b border-hull-800" id="flags">
        <Heading className="text-white">Available flags</Heading>
        <Lead className="mt-3 max-w-3xl">
          Every flag is optional and they combine freely. With none of them, a run
          behaves exactly as it did before this existed.
        </Lead>

        <div className="mt-8">
          <ScrollTable>
            <thead>
              <tr className="border-b border-hull-700">
                <th className="py-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-hull-400">
                  Flag
                </th>
                <th className="py-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-hull-400">
                  Framework
                </th>
                <th className="py-2.5 text-xs font-semibold uppercase tracking-wider text-hull-400">
                  Primary purpose
                </th>
              </tr>
            </thead>
            <tbody>
              {FLAGS.map((row) => (
                <tr key={row.flag} className="border-b border-hull-800">
                  <td className="py-2.5 pr-4 font-mono text-surge-300">{row.flag}</td>
                  <td className="py-2.5 pr-4 text-hull-200">{row.framework}</td>
                  <td className="py-2.5 text-hull-300">{row.purpose}</td>
                </tr>
              ))}
            </tbody>
          </ScrollTable>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <Card>
            <h3 className="text-base font-semibold text-white">
              <code className="font-mono text-surge-300">--security</code>
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-hull-300">
              The suites that apply to any web application, regardless of sector
              or jurisdiction: <code className="text-surge-400">--uwas</code>,{' '}
              <code className="text-surge-400">--cwsh</code> and{' '}
              <code className="text-surge-400">--owasp</code>. It deliberately
              does not pull in a regulatory framework — nobody typing{' '}
              <code className="text-surge-400">--security</code> is asking for a
              GDPR report.
            </p>
          </Card>
          <Card>
            <h3 className="text-base font-semibold text-white">
              <code className="font-mono text-surge-300">--compliance-all</code>
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-hull-300">
              Evaluates every installed framework and lets applicability decide.
              Frameworks with nothing to say about your application are reported{' '}
              <code className="text-surge-400">NOT_APPLICABLE</code> with the
              reason — a different, and much noisier, request than{' '}
              <code className="text-surge-400">--security</code>.
            </p>
          </Card>
        </div>

        <Card className="mt-4">
          <h3 className="text-base font-semibold text-white">Example commands</h3>
          <CommandBlock className="mt-3" label="Copy security and compliance examples">{`# the existing command, unchanged
waveqa -p "DemoProject" -f "Feature-01" --dev

# core web application security
waveqa -p "DemoProject" -f "Feature-01" --dev --uwas

# security headers only
waveqa -p "DemoProject" -f "Feature-01" --dev --cwsh

# the applicable security bundle
waveqa -p "DemoProject" -f "Feature-01" --dev --security

# a healthcare feature
waveqa -p "HealthApp" -f "PATIENT-101" --dev --uwas --cwsh --owasp --hipaa --privacy

# a payment feature
waveqa -p "PaymentApp" -f "PAY-101" --dev --uwas --cwsh --owasp --pci --privacy

# evaluate everything and let applicability decide
waveqa -p "DemoProject" -f "Feature-01" --dev --compliance-all`}</CommandBlock>
        </Card>
      </Section>

      {/* --- Inspecting the repository ------------------------------------ */}
      <Section className="border-b border-hull-800" id="inspect">
        <Heading className="text-white">Inspect what your agent ships</Heading>
        <Lead className="mt-3 max-w-3xl">
          Controls are data, versioned and installed with the agent. These two
          commands read that repository directly, so the answer is what your
          build will actually do rather than what this page says it does.
          Neither contacts a server, and neither runs anything.
        </Lead>

        <Card className="mt-8">
          <h3 className="font-mono text-base font-semibold text-white">
            waveqa compliance frameworks
          </h3>
          <p className="mt-2 text-sm font-medium text-hull-200">
            Every framework this build can verify, the flag that selects it, and
            how many controls it ships.
          </p>
          <p className="mt-2 text-sm leading-relaxed text-hull-300">
            The flags it prints are the same ones a run accepts &mdash; both are
            read from the repository, so a framework added as data brings its
            flag with it and the two cannot disagree. The counts are of controls{' '}
            <em>shipped</em>, not of controls a given run would evaluate: what
            runs is decided per page by applicability, and a framework that does
            not apply to your application resolves to one{' '}
            <code className="font-mono text-surge-300">NOT_APPLICABLE</code>{' '}
            block rather than a wall of verdicts.
          </p>
          <CommandBlock className="mt-4" label="Copy frameworks command">{`waveqa compliance frameworks`}</CommandBlock>
          <Code>{`Flag             Framework                  Version     Controls  In --security
--accessibility  WCAG Accessibility         2.2-AA            18
--ccpa           CCPA / CPRA                CPRA-2023          9
--cwsh           Common Web Security        1.0               16  yes
                 Headers
--owasp          OWASP ASVS                 5.0               34  yes
--uwas           Universal Web Application  1.0               46  yes
                 Security
...

249 controls across 11 frameworks.`}</Code>
        </Card>

        <Card className="mt-4">
          <h3 className="font-mono text-base font-semibold text-white">
            waveqa compliance controls &lt;framework&gt;
          </h3>
          <p className="mt-2 text-sm font-medium text-hull-200">
            One framework&rsquo;s controls, and how each of them can be settled.
          </p>
          <p className="mt-2 text-sm leading-relaxed text-hull-300">
            The <strong className="text-hull-200">Settled by</strong> column is
            the one worth reading, and worth reading <em>before</em> a run rather
            than after. Automated verification cannot establish a policy, a
            training record, a risk assessment or a supplier review &mdash; most
            of ISO/IEC 27001 is exactly that &mdash; so those controls are named
            here up front and reported{' '}
            <code className="font-mono text-surge-300">EVIDENCE_REQUIRED</code>{' '}
            or <code className="font-mono text-surge-300">NOT_UI_TESTABLE</code>{' '}
            in the run, never passed. ISO/IEC 27001 ships 26 controls; 7 of them
            an application can evidence.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-hull-300">
            Take the framework id from the flag without its dashes:{' '}
            <code className="text-surge-400">owasp</code>,{' '}
            <code className="text-surge-400">hipaa</code>,{' '}
            <code className="text-surge-400">cwsh</code>,{' '}
            <code className="text-surge-400">iso27001</code>. Add{' '}
            <code className="text-surge-400">--category</code> to narrow a long
            framework to one area.
          </p>
          <CommandBlock className="mt-4" label="Copy controls commands">{`waveqa compliance controls owasp

# one area of a long framework
waveqa compliance controls cwsh --category Transport

# what a framework will not settle for you
waveqa compliance controls iso27001`}</CommandBlock>
          <Code>{`Control   Severity  Category          Settled by  Title
CWSH-001  MEDIUM    Transport         automated   Strict-Transport-Security is set
CWSH-002  LOW       Transport         automated   HSTS covers subdomains

Common Web Security Headers 1.0 - 2 control(s), 2 automated, 0 needing
evidence or review.`}</Code>
        </Card>

        <p className="mt-6 max-w-3xl text-sm text-hull-400">
          Every other command is on the{' '}
          <Link className="text-surge-400 hover:text-surge-300" to="/docs/commands">
            command reference
          </Link>
          , and <code className="text-surge-400">waveqa guide</code> prints the
          same grouping in your terminal.
        </p>
      </Section>

      {/* --- Frameworks --------------------------------------------------- */}
      <Section className="border-b border-hull-800" id="frameworks">
        <Heading className="text-white">What each framework checks</Heading>
        <Lead className="mt-3 max-w-3xl">
          Controls live in a versioned repository, not in the execution engine.
          The framework version each run used is printed in its report.
        </Lead>

        <div className="mt-8 space-y-3">
          <Disclosure
            summary="Universal Web Application Security — --uwas"
            count="46 controls · v1.0"
          >
            <p className="mb-5 text-sm leading-relaxed text-hull-300">
              Core web application security, mapped to OWASP Top 10 (2021) and
              ASVS 5.0. Controls marked <em>active</em> can only be settled by
              sending the application input it did not ask for; they are named in
              full and reported as EVIDENCE_REQUIRED rather than performed. See{' '}
              <a href="#active" className="text-surge-400 underline">
                active probes
              </a>
              .
            </p>
            <div className="grid gap-6 sm:grid-cols-2">
              {UWAS_GROUPS.map(([title, items]) => (
                <div key={title as string}>
                  <h4 className="mb-2 text-sm font-semibold text-white">{title}</h4>
                  <Checklist items={items as string[]} />
                </div>
              ))}
            </div>
          </Disclosure>

          <Disclosure
            summary="Common Web Security Headers — --cwsh"
            count="16 controls · v1.0"
          >
            <p className="mb-5 text-sm leading-relaxed text-hull-300">
              The actual HTTP response headers are captured as evidence for every
              page observed. Each header below is checked for meaningful
              configuration, not merely for presence.
            </p>
            <ScrollTable>
              <thead>
                <tr className="border-b border-hull-700">
                  <th className="py-2 pr-4 text-xs font-semibold uppercase tracking-wider text-hull-400">
                    Header
                  </th>
                  <th className="py-2 pr-4 text-xs font-semibold uppercase tracking-wider text-hull-400">
                    Control
                  </th>
                  <th className="py-2 text-xs font-semibold uppercase tracking-wider text-hull-400">
                    Purpose and expected behaviour
                  </th>
                </tr>
              </thead>
              <tbody>
                {HEADERS.map(([header, id, body]) => (
                  <tr key={header} className="border-b border-hull-800">
                    <td className="py-2.5 pr-4 align-top font-mono text-xs text-surge-300">
                      {header}
                    </td>
                    <td className="py-2.5 pr-4 align-top font-mono text-xs text-hull-400">
                      {id}
                    </td>
                    <td className="py-2.5 align-top text-hull-300">{body}</td>
                  </tr>
                ))}
              </tbody>
            </ScrollTable>
          </Disclosure>

          <Disclosure summary="OWASP ASVS — --owasp" count="34 controls · v5.0">
            <p className="mb-5 text-sm leading-relaxed text-hull-300">
              Organised by the ASVS 5.0 chapter structure, so a finding can be
              taken to a team already working from it. WaveQA implements the
              controls verifiable from observation of a running application;
              chapters covering source code, cryptographic implementation and
              build configuration are reported as EVIDENCE_REQUIRED rather than
              assessed.
            </p>
            <Pills items={ASVS_CHAPTERS} />
            <p className="mt-5 text-xs text-hull-400">
              Supported version: ASVS 5.0. The control repository is versioned;
              this page is updated when it is.
            </p>
          </Disclosure>

          <Disclosure summary="General Privacy — --privacy" count="14 controls · v1.0">
            <p className="mb-5 text-sm leading-relaxed text-hull-300">
              Law-independent personal-data protection. For every sensitive field
              the run asks whether it is collected, necessary, displayed, masked,
              transmitted, stored, logged, exported, deleted and authorised.
            </p>
            <p className="text-sm leading-relaxed text-hull-300">
              Detected categories: name, email, phone, address, date of birth,
              national identifier, passport, financial data, medical data,
              location, device identifiers and authentication information.
              Detection is by field name and page structure — never by reading and
              recording the values, because a classifier that stores the personal
              data it finds is its own privacy incident.
            </p>
          </Disclosure>

          <Disclosure summary="GDPR — --gdpr" count="24 controls · 2016/679">
            <Topics rows={GDPR_TOPICS} />
            <Warning>
              GDPR compliance is a legal determination about an organisation&rsquo;s
              processing, its lawful bases, its records and its contracts. WaveQA
              verifies the part a web application can demonstrate. A clean result
              here is not a compliance opinion.
            </Warning>
          </Disclosure>

          <Disclosure summary="CCPA / CPRA — --ccpa" count="9 controls · CPRA 2023">
            <Topics
              rows={[
                ['Notice at collection', 'Reachable at the point of collection; categories, purposes, sale/sharing status and retention stated.'],
                ['Do Not Sell or Share', 'A clear and conspicuous link, or the Your Privacy Choices control.'],
                ['Opt-out preference signals', 'Global Privacy Control honoured as a valid opt-out. Verified with the header set, not from platform configuration.'],
                ['No dark patterns', 'Opting out must take no more steps and be no less prominent than opting in.'],
                ['Sensitive information', 'A limit-use control where sensitive personal information is used beyond permitted purposes.'],
                ['Consumer rights', 'Two or more designated methods for know, delete and correct requests, and no penalty for exercising them.'],
              ]}
            />
            <Warning>
              Whether the CCPA/CPRA applies at all depends on revenue, data volume
              and California nexus — none of which WaveQA can observe. These
              controls verify the application-side obligations only.
            </Warning>
          </Disclosure>

          <Disclosure summary="HIPAA — --hipaa" count="21 controls · 45 CFR 164">
            <Topics rows={HIPAA_TOPICS} />
            <Warning>
              <strong className="text-white">
                Automated WaveQA testing does not establish HIPAA compliance.
              </strong>{' '}
              The Security Rule requires administrative and physical safeguards, a
              risk analysis, workforce training and business associate agreements
              — none of which a web application can demonstrate. These controls
              verify technical safeguards observable in the application.
            </Warning>
          </Disclosure>

          <Disclosure summary="PCI DSS — --pci" count="21 controls · v4.0.1">
            <Topics rows={PCI_TOPICS} />
            <Warning>
              A PCI DSS assessment is performed by a QSA, or through a
              Self-Assessment Questionnaire, against the whole cardholder data
              environment — networks, storage, key management, physical access and
              policy. WaveQA verifies application-layer requirements only. It is
              not an assessment, an Attestation of Compliance, or an ASV scan.
            </Warning>
          </Disclosure>

          <Disclosure summary="Financial Security — --finance" count="20 controls · v1.0">
            <Topics
              rows={[
                ['Account data', 'Identifiers masked where displayed, absent from URLs, not cached, not in web storage, carried over TLS, and statements authorised per account.'],
                ['Transactions', 'Server-side amount and currency validation, transaction limits, duplicate and replay protection, step-up authentication for high-risk transactions, an immutable audit trail, and confirmation before execution.'],
                ['Administration', 'Privileged access separated, MFA enforced, separation of duties through a maker-checker workflow, and administrative actions logged.'],
              ]}
            />
            <Warning>
              Financial regulation is jurisdiction-specific and depends on the
              institution&rsquo;s licence, product and supervisor. These controls
              are a technical baseline, not a regulatory checklist for any
              particular regime. Identifying which regime applies is recorded as
              its own EVIDENCE_REQUIRED control.
            </Warning>
          </Disclosure>

          <Disclosure summary="ISO/IEC 27001 — --iso27001" count="26 controls · 2022">
            <Warning>
              Some ISO/IEC 27001 requirements cannot be validated through web
              application automation. WaveQA marks these controls as
              EVIDENCE_REQUIRED or NOT_UI_TESTABLE rather than automatically
              passing them.
            </Warning>
            <div className="mt-6 grid gap-8 md:grid-cols-2">
              <div>
                <h4 className="mb-3 text-sm font-semibold text-white">
                  Application-testable controls
                </h4>
                <p className="mb-3 text-xs text-hull-400">
                  Partly settled by the run. Every one still requires its
                  documentary half — a working access check with no governing
                  policy does not satisfy A.5.15.
                </p>
                <Topics rows={ISO_TESTABLE} />
              </div>
              <div>
                <h4 className="mb-3 text-sm font-semibold text-white">
                  Evidence / organisation controls
                </h4>
                <p className="mb-3 text-xs text-hull-400">
                  Reported as NOT_UI_TESTABLE with the records that would settle
                  them. Never passed.
                </p>
                <ul className="space-y-1.5">
                  {ISO_ORGANISATIONAL.map((item) => (
                    <li key={item} className="flex gap-2.5 text-sm text-hull-300">
                      <span aria-hidden="true" className="mt-0.5 shrink-0 text-hull-500">
                        ○
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Disclosure>

          <Disclosure
            summary="Accessibility — --accessibility"
            count="18 controls · WCAG 2.2 AA"
          >
            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <h4 className="mb-3 text-sm font-semibold text-white">
                  Checked automatically
                </h4>
                <Checklist items={WCAG_AUTOMATED} />
              </div>
              <div>
                <h4 className="mb-3 text-sm font-semibold text-white">
                  Requires a person
                </h4>
                <p className="mb-3 text-xs text-hull-400">
                  Reported as EVIDENCE_REQUIRED with what would settle each.
                </p>
                <ul className="space-y-1.5">
                  {WCAG_HUMAN.map((item) => (
                    <li key={item} className="flex gap-2.5 text-sm text-hull-300">
                      <span aria-hidden="true" className="mt-0.5 shrink-0 text-hull-500">
                        ○
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <Warning>
              Automated testing reaches roughly a third of WCAG success criteria.
              A page can satisfy every automated check above and still be unusable
              with a screen reader.
            </Warning>
          </Disclosure>
        </div>
      </Section>

      {/* --- Scoping ------------------------------------------------------ */}
      <Section className="border-b border-hull-800" id="scope">
        <Heading className="text-white">Which controls run where</Heading>
        <Lead className="mt-3 max-w-3xl">
          Running every control on every page buries the twelve findings that
          matter under four hundred that do not. Applicability is decided before
          anything executes.
        </Lead>

        <Card className="mt-8">
          <pre className="overflow-x-auto font-mono text-xs leading-relaxed text-hull-200">
            <code>{`Framework  +  Page type  +  Data classification  +  Feature type
           +  Application configuration  +  Jurisdiction
= Applicable controls`}</code>
          </pre>
        </Card>

        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          <Card>
            <h3 className="text-base font-semibold text-white">Page types</h3>
            <p className="mb-4 mt-2 text-sm text-hull-300">
              Each page the run reaches is classified from its path, its headings
              and its form fields. The signals that produced a classification are
              printed in the report, so it can be checked rather than taken on
              trust.
            </p>
            <Pills items={PAGE_TYPES} />
          </Card>
          <Card>
            <h3 className="text-base font-semibold text-white">
              Data classifications
            </h3>
            <p className="mb-4 mt-2 text-sm text-hull-300">
              What a page handles is what brings a framework into scope. The
              hierarchy is applied automatically: ePHI implies PHI implies
              sensitive personal data implies personal data.
            </p>
            <Pills items={DATA_TYPES} />
          </Card>
        </div>

        <Card className="mt-4">
          <h3 className="text-base font-semibold text-white">Worked example</h3>
          <pre className="mt-3 overflow-x-auto font-mono text-xs leading-relaxed text-hull-200">
            <code>{`Feature   Patient Record
Page      /patients/123
Data      PHI + PERSONAL_DATA
Flags     --uwas --hipaa --gdpr

Applicable
  UWAS    authentication · authorization · session · input · API · data protection
  HIPAA   access control · PHI protection · audit · transmission security
  GDPR    personal data · minimisation · access · erasure · privacy notice`}</code>
          </pre>
          <p className="mt-4 text-sm text-hull-300">
            Run the same flags against a timesheet application and HIPAA reports{' '}
            <code className="text-surge-400">0 passed, 0 failed, 21 N/A</code>{' '}
            with one stated reason — which is the correct and useful answer.
          </p>
        </Card>

        <Card className="mt-4">
          <h3 className="text-base font-semibold text-white">
            Which pages are looked at
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-hull-300">
            The environment address from your readme, the pages the functional run
            actually navigated to while verifying criteria, and the documented{' '}
            <code className="text-surge-400">GET</code> routes. WaveQA does not
            crawl, does not follow links, and does not guess at paths — which is
            what keeps it safe to run against an application you may not own.
          </p>
        </Card>
      </Section>

      {/* --- Testability -------------------------------------------------- */}
      <Section className="border-b border-hull-800" id="testability">
        <Heading className="text-white">UI, API, network, evidence</Heading>
        <Lead className="mt-3 max-w-3xl">
          Every control declares what kind of evidence could settle it. This is
          the classification that stops a policy requirement being reported as
          passing because a page loaded.
        </Lead>

        <dl className="mt-8 space-y-3">
          {TEST_KINDS.map(([term, body]) => (
            <div
              key={term}
              className="flex flex-col gap-1 border-l-2 border-hull-700 pl-4 sm:flex-row sm:gap-6"
            >
              <dt className="w-44 shrink-0 font-mono text-sm font-semibold text-surge-300">
                {term}
              </dt>
              <dd className="text-sm text-hull-300">{body}</dd>
            </div>
          ))}
        </dl>

        <Card className="mt-8" id="llm">
          <h3 className="text-base font-semibold text-white">Where the model is used</h3>
          <p className="mt-2 text-sm leading-relaxed text-hull-300">
            Headers, cookies, status codes, TLS, DOM attributes, URL shapes,
            presence and absence are decided by code. They have determinate
            answers, and a model asked for one would sometimes be confidently
            wrong about a fact anyone can look up.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-hull-300">
            The model is asked only interpretive questions — whether a privacy
            notice actually explains its stated purposes, whether an error message
            reveals more than it needed to, whether a page collects more personal
            data than its purpose requires. Each decision is recorded with its
            input, the control, a short rationale, the decision and a confidence.
            Below the confidence floor the reading is attached as a note and the
            control is reported EVIDENCE_REQUIRED rather than settled.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-hull-300">
            With no model configured, those controls report EVIDENCE_REQUIRED.
            They never default to passing.
          </p>
        </Card>

        <Card className="mt-4" id="active">
          <h3 className="text-base font-semibold text-white">Active probes</h3>
          <p className="mt-2 text-sm leading-relaxed text-hull-300">
            Some controls — injection, brute force, privilege escalation, IDOR —
            can only be settled by sending the application an attack. They are
            real controls and they are named in the report in full, with the
            technique each would require. WaveQA does not perform them on the
            strength of a framework flag.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-hull-300">
            Enabling them requires both{' '}
            <code className="text-surge-400">security: active_probes: true</code>{' '}
            in the project&rsquo;s <code className="text-surge-400">waveqa.yml</code>{' '}
            and <code className="text-surge-400">--active-probes</code> on the
            command line, and is refused against production regardless. Without
            that authorisation each is reported EVIDENCE_REQUIRED — which is
            honest, and is not the same as pretending it passed.
          </p>
        </Card>
      </Section>

      {/* --- Result states ------------------------------------------------ */}
      <Section className="border-b border-hull-800" id="states">
        <Heading className="text-white">Result states</Heading>
        <Lead className="mt-3 max-w-3xl">
          Eight, deliberately not two. Collapsing them into pass and fail is the
          failure mode compliance tooling is notorious for.
        </Lead>

        <ul className="mt-8 space-y-3">
          {STATES.map(([state, tone, body]) => (
            <li
              key={state}
              className="flex flex-col gap-1 border-l-2 border-hull-700 pl-4 sm:flex-row sm:gap-6"
            >
              <span className={`w-48 shrink-0 font-mono text-sm font-semibold ${tone}`}>
                {state}
              </span>
              <span className="text-sm text-hull-300">{body}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* --- Evidence and reporting --------------------------------------- */}
      <Section className="border-b border-hull-800" id="evidence">
        <Heading className="text-white">Evidence and reporting</Heading>
        <Lead className="mt-3 max-w-3xl">
          Every automated result cites the artefact it came from, and the artefact
          is in your run folder.
        </Lead>

        <Card className="mt-8">
          <h3 className="text-base font-semibold text-white">What is captured</h3>
          <div className="mt-4">
            <Pills items={EVIDENCE} />
          </div>
          <p className="mt-4 text-sm leading-relaxed text-hull-300">
            Sensitive values are named, never copied. A finding about an unmasked
            card number reports the count and the kind; a finding about a token in
            browser storage reports the key and the value <em>shape</em>. A
            compliance report that reproduces the data it objects to has made the
            problem worse.
          </p>
        </Card>

        <Card className="mt-4">
          <h3 className="text-base font-semibold text-white">
            A control in the report
          </h3>
          <pre className="mt-3 overflow-x-auto font-mono text-xs leading-relaxed text-hull-200">
            <code>{`Control        CWSH-001
Framework      Common Web Security Headers 1.0
Category       Transport
Page           https://dev.example.com/login
Testability    API_TESTABLE, AUTOMATION_TESTABLE

Requirement    HTTPS responses must instruct the browser to use
               HTTPS for subsequent requests to this host.
Expected       Strict-Transport-Security present with a max-age
               of at least 10000 seconds.
Actual         Strict-Transport-Security: max-age=31536000
Result         PASS
Evidence       evidence/compliance-login-response.json`}</code>
          </pre>
        </Card>

        <Card className="mt-4">
          <h3 className="text-base font-semibold text-white">The summary table</h3>
          <pre className="mt-3 overflow-x-auto font-mono text-xs leading-relaxed text-hull-200">
            <code>{`Framework                       Passed  Failed  Blocked  Evidence  N/A
─────────────────────────────────────────────────────────────────────
Universal Web Security              35       2        1        18     4
Security Headers                     8       1        0         0     2
OWASP ASVS                          42       3        2        12     7
GDPR                                15       1        0         8     3
HIPAA                                0       0        0         0    21
PCI DSS                              0       0        0         0    21`}</code>
          </pre>
          <p className="mt-4 text-sm leading-relaxed text-hull-300">
            Only frameworks you selected, or that your project configuration
            enabled, appear. The HIPAA and PCI rows above are the correct output
            for an application that handles neither health nor card data: they were
            considered and found out of scope, which is different from never having
            been looked at.
          </p>
        </Card>
      </Section>

      {/* --- Configuration ------------------------------------------------ */}
      <Section className="border-b border-hull-800" id="configuration">
        <Heading className="text-white">Project configuration</Heading>
        <Lead className="mt-3 max-w-3xl">
          Optional. Put <code className="font-mono text-surge-300">waveqa.yml</code>{' '}
          in the project root to stop typing the same flags, and to record the two
          things the agent cannot observe.
        </Lead>

        <Card className="mt-8">
          <CommandBlock label="Copy waveqa.yml example">{`security:
  enabled: true

  frameworks:
    owasp:
      enabled: true
    gdpr:
      enabled: true
    hipaa:
      enabled: false
    iso27001:
      enabled: true

  # What this product handles, even on pages that do not show it.
  data_classification:
    - personal_data

  # Where it operates. Controls scoped elsewhere become NOT_APPLICABLE.
  jurisdiction:
    - EU`}</CommandBlock>
          <p className="mt-4 text-sm leading-relaxed text-hull-300">
            CLI flags <strong className="text-white">add to</strong> this and never
            subtract from it. There is deliberately no way for a command line to
            switch off a framework the project turned on — compliance configuration
            that a passing command can quietly disable is compliance theatre.
          </p>
        </Card>
      </Section>

      {/* --- Limitations -------------------------------------------------- */}
      <Section className="border-b border-hull-800" id="limitations">
        <Heading className="text-white">Limitations</Heading>
        <Lead className="mt-3 max-w-3xl">
          Worth reading before you rely on a green section.
        </Lead>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {[
            {
              title: 'Observation, not scanning',
              body: 'Everything is drawn from pages your own documents named and the run already reached. WaveQA does not crawl, enumerate paths, or fuzz. Coverage is bounded by where your acceptance criteria took it.',
            },
            {
              title: 'Not a penetration test',
              body: 'Injection, privilege escalation, session fixation and rate limiting need an authorised active engagement. They are named and reported as unsettled, not attempted.',
            },
            {
              title: 'Point-in-time, per environment',
              body: 'A header set on some routes and not others is only detected on the routes observed. A local run cannot observe transport security at all, and says so rather than passing.',
            },
            {
              title: 'Organisational controls are out of reach',
              body: 'Policy, risk assessment, training, supplier management, continuity and audit are established by document review. WaveQA lists them and what would settle them.',
            },
            {
              title: 'Classification is conservative',
              body: 'A page with no distinguishing signal is classified narrowly, which brings fewer controls into scope. Under-claiming loses coverage; over-claiming invents findings someone has to disprove.',
            },
            {
              title: 'Interpretation has a confidence floor',
              body: 'Where a model reads prose against a requirement and is not confident, the control is reported EVIDENCE_REQUIRED with the reading attached — never as a verdict.',
            },
          ].map((note) => (
            <Card key={note.title}>
              <h3 className="text-base font-semibold text-white">{note.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-hull-300">{note.body}</p>
            </Card>
          ))}
        </div>
      </Section>

      {/* --- Disclaimer --------------------------------------------------- */}
      <Section id="disclaimer">
        <Heading className="text-white">Regulatory disclaimer</Heading>
        <Card className="mt-6 border-warn-600/40 bg-warn-600/5">
          <p className="text-sm leading-relaxed text-hull-200">
            WaveQA provides automated security, privacy, compliance and
            accessibility verification of web applications. Automated test results
            are technical verification evidence and do not by themselves
            constitute legal compliance, certification, accreditation,
            penetration-testing certification, or regulatory approval.
            Requirements must be interpreted according to the applicable framework
            version, jurisdiction, organisation, application scope, contracts and
            regulatory guidance.
          </p>
        </Card>
        <p className="mt-6 max-w-3xl text-sm text-hull-400">
          Framework versions in this release: Universal Web Security 1.0, Web
          Security Headers 1.0, OWASP ASVS 5.0, Privacy 1.0, GDPR (Regulation (EU)
          2016/679), CCPA/CPRA 2023, HIPAA Security Rule (45 CFR Part 164), PCI DSS
          4.0.1, Financial Security 1.0, ISO/IEC 27001:2022, WCAG 2.2 Level AA.
          Each control cites its source, and each result records the framework
          version it was evaluated under.
        </p>
      </Section>
    </>
  );
}
